import { ButtonBase } from '@mui/material';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dropdown, IconButton, Stack, styled, Text } from 'shell/components';
import { CourseLobbyLayout, ViewportDevice } from 'utils/constants';

const CoverImage = ({ course, CoverImageContainer }) => {
    return (
        <CoverImageContainer color={course.cover_color || 'purple'}>
            <svg
                width='438'
                height='182'
                viewBox='0 0 438 182'
                fill='none'
                role='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M256.78 -105.069C263.258 -169.148 308.58 -219.822 366.906 -236.417L373.512 -301.757C280.079 -286.263 204.612 -210.136 194.623 -111.324C182.676 6.861 268.758 112.215 386.994 124.114C485.504 134.027 575.048 75.6445 609.088 -12.4098L543.72 -18.9882C515.611 34.4606 457.151 68.4118 393.389 61.995C309.558 53.5585 248.424 -21.2616 256.895 -105.057L256.78 -105.069Z'
                    fill='white'
                    fillOpacity='0.1'
                />
                <path
                    d='M173.621 273.539C178.674 337.744 143.111 395.653 88.673 422.336L93.8251 487.805C183.007 455.974 243.695 367.668 235.904 268.661C226.585 150.244 123.086 61.8542 4.61167 71.133C-94.0976 78.8638 -171.803 152.208 -189.598 244.898L-124.099 239.768C-105.969 182.183 -54.4951 138.398 9.39583 133.394C93.3964 126.815 166.899 189.588 173.507 273.548L173.621 273.539Z'
                    fill='white'
                    fillOpacity='0.1'
                />
            </svg>
        </CoverImageContainer>
    );
};

const CardInformation = ({ course, device, layoutConfig, handleCourseCardDropdown }) => {
    const { message } = useSnackbar();
    const { t } = useTranslation('course');
    const { InformationContainer, CourseMeta, isListLayout, titleMaxLines } = layoutConfig;

    const handleClickCopyCourseID = async (e) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(course.generated_id);
        message.success(t('course-lobby.message.course-id-copied-success', 'Course ID copied to clipboard.'));
    };

    return (
        <InformationContainer
            alignItems={course.role === 'instructor' && !isListLayout ? 'flex-start' : 'center'}
            direction={'row'}
            justifyContent={'space-between'}
            device={device}
        >
            <CourseMeta>
                <Text
                    component={'p'}
                    maxLines={titleMaxLines}
                    variant={isListLayout ? 'sh2' : 'sh1'}
                    weight={'semibold'}
                    tooltip={{ component: course.name || t('course-lobby.no-name-course', 'No Name'), maxWidth: 250 }}
                >
                    {course.name || t('course-lobby.no-name-course', 'No Name')}
                </Text>
                {course.role === 'instructor' && (
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text weight={'medium'}>{t('course-lobby.course-id', 'Course ID')}</Text>
                        <Button variant={'borderless'} onClick={handleClickCopyCourseID}>
                            {course.generated_id}
                        </Button>
                        <IconButton
                            aria-label={'copy course id'}
                            kind={'copy'}
                            size={'small'}
                            variant={'square'}
                            onClick={handleClickCopyCourseID}
                        />
                    </Stack>
                )}
            </CourseMeta>
            {course.role === 'instructor' && (
                <Dropdown
                    id={`course-card-dropdown-${course.course_id}`}
                    options={[
                        {
                            key: 'archive',
                            value: course.is_archived
                                ? t('course-lobby.actions.unarchive', 'Unarchive course')
                                : t('course-lobby.actions.archive', 'Archive course'),
                        },
                        { key: 'duplicate-course', value: t('course-lobby.actions.duplicate', 'Duplicate course') },
                        { key: 'delete', value: t('course-lobby.actions.delete', 'Delete course') },
                    ]}
                    rootComponent={
                        <IconButton
                            aria-label={'more options'}
                            kind={'moreHorizontal'}
                            size={'medium'}
                            variant={'square'}
                        />
                    }
                    onSelect={({ key }) => handleCourseCardDropdown({ key, courseId: course.course_id })}
                />
            )}
        </InformationContainer>
    );
};

const CourseCard = (props) => {
    const { device } = useViewport();
    const layout = props.layout || CourseLobbyLayout.GRID;
    const layoutConfig = COURSE_CARD_LAYOUT_CONFIG[layout] ?? COURSE_CARD_LAYOUT_CONFIG[CourseLobbyLayout.GRID];
    const { RootButton, CoverImageContainer } = layoutConfig;

    return (
        <RootButton
            className={props.className}
            component={'div'}
            device={device}
            role={'presentation'}
            onClick={() => props.onClick(props.course)}
        >
            <CoverImage {...props} CoverImageContainer={CoverImageContainer} />
            <CardInformation {...props} device={device} layoutConfig={layoutConfig} />
        </RootButton>
    );
};

const CourseButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        display: 'flex',
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '20px',
        boxShadow: theme.palette.ensightful.shadows[1],
        overflow: 'hidden',
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[500],
        },
        '&:active': {
            borderColor: theme.palette.ensightful.primary[700],
        },
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const GridRootButton = styled(CourseButtonBase)(({ device }) => {
    return {
        flexDirection: 'column',
        height: '286px',

        ...(device === ViewportDevice.MOBILE && {
            width: '100%',
        }),
        ...(device === ViewportDevice.TABLET && {
            width: '358px',
        }),
        ...(device === ViewportDevice.DESKTOP && {
            width: '440px',
        }),
    };
});

const ListRootButton = styled(CourseButtonBase)(({ theme, device }) => {
    return {
        alignItems: 'stretch',
        width: '100%',
        minHeight: theme.spacing(22),
        background: theme.palette.ensightful.shades[0],

        ...(device === ViewportDevice.MOBILE && {
            minHeight: '128px',
        }),
    };
});

const BaseCoverImageContainer = styled(Box)(({ theme, color }) => {
    return {
        ...(color === 'purple' && {
            background: theme.palette.ensightful.primary[500],
        }),
        ...(color === 'green' && {
            background: theme.palette.ensightful.success[800],
        }),
        ...(color === 'red' && {
            background: theme.palette.ensightful.error[400],
        }),
        ...(color === 'dark_blue' && {
            background: theme.palette.ensightful.secondary[700],
        }),
        ...(color === 'yellow' && {
            background: theme.palette.ensightful.warning[400],
        }),
    };
});

const GridCoverImageContainer = styled(BaseCoverImageContainer)(() => {
    return {
        borderRadius: '19px 19px 0 0',
        width: '100%',
        height: '182px',
    };
});

const ListCoverImageContainer = styled(BaseCoverImageContainer)(({ theme }) => {
    const LIST_IMAGE_SIZE = '30px';

    return {
        borderRadius: '50%',
        height: LIST_IMAGE_SIZE,
        width: LIST_IMAGE_SIZE,
        marginLeft: theme.spacing(5),
        marginBlock: 'auto',
        overflow: 'hidden',
        flexShrink: 0,

        '& svg': {
            height: '100%',
            width: '130%',
        },
    };
});

const CourseMetaBase = styled(Stack)(() => {
    return {
        minWidth: 0,
        width: '100%',
    };
});

const GridCourseMeta = styled(CourseMetaBase)(({ theme }) => {
    return {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
    };
});

const ListCourseMeta = styled(CourseMetaBase)(({ theme }) => {
    return {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    };
});

const CardInformationContainerBase = styled(Stack)(({ theme }) => {
    return {
        flex: 1,
        minWidth: 0,
        borderRadius: '0 0 8px 8px',
        width: '100%',
        padding: theme.spacing(5),
    };
});

const GridCardInformationContainer = styled(CardInformationContainerBase)(({ theme }) => {
    return {
        height: '102px',
        gap: theme.spacing(4),
    };
});

const ListCardInformationContainer = styled(CardInformationContainerBase)(({ theme, device }) => {
    return {
        height: 'auto',
        gap: theme.spacing(2),

        ...(device === ViewportDevice.MOBILE && {
            paddingBlock: theme.spacing(3),
            paddingLeft: theme.spacing(4),
        }),
    };
});

const COURSE_CARD_LAYOUT_CONFIG = {
    [CourseLobbyLayout.GRID]: {
        RootButton: GridRootButton,
        CoverImageContainer: GridCoverImageContainer,
        InformationContainer: GridCardInformationContainer,
        CourseMeta: GridCourseMeta,
        titleMaxLines: 1,
        isListLayout: false,
    },
    [CourseLobbyLayout.LIST]: {
        RootButton: ListRootButton,
        CoverImageContainer: ListCoverImageContainer,
        InformationContainer: ListCardInformationContainer,
        CourseMeta: ListCourseMeta,
        titleMaxLines: 2,
        isListLayout: true,
    },
};

export default CourseCard;
